import { Link } from 'react-router-dom'
import { LEGAL, PRIVACY_PATH } from '../lib/legal'
import { LegalPage, Fact, ContactEmail } from '../components/legal/legal-page'

/**
 * Terms of Service (SOL-197). DRAFT for legal review.
 *
 * Every claim here describes what the code does today, not what is planned:
 * yearly renewal paid by hand, never charged automatically, and the handover
 * of a whole Memoir to the client (SOL-185); renewal prices depend on size,
 * but no size limit is ever named here (SOL-199's copy rule);
 * no end-to-end encryption: photos are encrypted in storage with a key per
 * Memoir (SOL-203, R2 SSE-C) and Memoar's systems can decrypt them. Where the product's behaviour is a
 * business decision nobody has made, the text says so with <Pending> rather
 * than inventing one. Change the code and this together.
 */
export default function Terms(): JSX.Element {
  return (
    <LegalPage title="Terms of Service">
      <section>
        <h2>1. Who we are</h2>
        <p>
          Memoar is operated by {LEGAL.operator}, {LEGAL.operatorDescription}, at{' '}
          <Fact value={LEGAL.address} label="registered address" /> ("we", "us"). You can reach us
          at <ContactEmail />.
        </p>
        <p>
          These terms apply when you buy a Memoir, sign in to Memoar, or open something that has
          been shared with you through it. How we handle personal data is described in our{' '}
          <Link to={PRIVACY_PATH}>Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <h2>2. What you buy</h2>
        <p>
          A Memoir is a private space for one photographic delivery: you upload the photographs,
          organise them into albums, and share them with the people they are for. Each Memoir is a
          separate purchase of €30 plus VAT, which includes its first year, in which you can add
          photographs.
        </p>
        <p>
          From the second year, whoever owns the Memoir pays for it a year at a time, to keep it:
          either a year in which it can only be viewed, downloaded and shared, or a year in which
          photographs can still be added. The yearly price depends on how much the Memoir holds and
          is shown before you pay. You can pay for the next year from the app in the 90 days
          before your paid year ends, and we email you about 90 and about 21 days before it does.
          You can also pay at any time for a year in which photographs can be added, if your
          Memoir is read-only. Nothing is charged automatically: there is no subscription, and
          each year is paid only if you choose to pay it. What happens if a year
          is not paid is described in section 7.
        </p>
        <p>
          VAT is calculated at checkout from your billing address. If you buy as a business and
          enter a valid VAT number, the reverse-charge rules may apply and no VAT is added. Payment
          is processed by Stripe, which also issues your receipt and invoice.
        </p>
        <p>
          Your first purchase creates your Memoar account under the email address you pay with.
          You sign in with a one-time code we email to that address, so anyone who can read that
          mailbox can sign in to your account. Keep it secure.
        </p>
      </section>

      <section>
        <h2>3. Your photographs</h2>
        <p>
          You keep every right you have in what you upload. You give us only the permission we
          need to provide the service: to store your files, to make smaller copies of them for
          display, and to show and deliver them to you and to whoever you share them with. That
          permission ends when the files are deleted.
        </p>
        <p>
          You confirm that you have the right to upload and share what you upload, including any
          permission you need from the people who appear in it. You may not use Memoar for
          content that is unlawful, that infringes someone else's rights, or that sexualises
          minors. We may remove such content and close the account that uploaded it.
        </p>
        <p>
          We do not sell your photographs, use them to train AI models, or show them to anyone you
          have not shared them with. See the Privacy Policy for the narrow cases in which we can
          access them.
        </p>
      </section>

      <section>
        <h2>4. Sharing</h2>
        <p>
          You can share an album in two ways. A <strong>share code</strong> works once and expires
          after the time you choose; whoever redeems it gains access to the album in their own
          Memoar account, while you remain its owner. A <strong>view link</strong> lets anyone who
          has it view the album without an account, until you revoke it or it expires. You decide
          who receives a code or a link, and you are responsible for that choice.
        </p>
        <p>
          You can also <strong>share a whole Memoir</strong> with your client by entering their
          email address. If they do not yet have a Memoar account, this creates one for that
          address, so that they can sign in with a code sent there. Only the person at that
          address can accept; they can accept or decline, and until the Memoir moves to them you
          can withdraw the invitation. Once they have accepted, they can view and download the
          whole Memoir, and they also receive the emails about paying for its next year. A Memoir
          can be shared this way with one person at a time, and only while its purchase is paid
          for.
        </p>
        <p>
          The Memoir then becomes theirs, with everything in it, either when you hand it over or
          automatically when your paid year ends, if they have accepted by then. While an
          invitation is open the Memoir cannot be renewed, so that the move happens at the end of
          the year. From the move, they are its owner: they receive every notice, decide who else
          sees it, and pay for its later years. If it is paid for at the time, it stays paid until
          the end of the year you paid for, but it is read-only for them until they pay for a year
          in which photographs can be added. Your account no longer has access to it. View links
          you created keep working, for whoever has them, until the new owner revokes them. Share
          codes for its albums that you created before the move and that nobody has used stop
          working, and people who already redeemed a code keep their albums.
        </p>
      </section>

      <section>
        <h2>5. Limits</h2>
        <p>
          A single file can be up to 500 MB. Each Memoir also has a technical limit on total size,
          set far above any ordinary photographic delivery, to protect the service from runaway or
          abusive uploads. If you reach it, further uploads are refused and nothing already
          stored is affected.
        </p>
      </section>

      <section>
        <h2>6. Refunds and cancellation</h2>
        <p>
          You can have any Memoir refunded in full within 30 days of buying it, for any reason.
          Refund it from the Memoir's page in the app, or write to <ContactEmail /> with the email
          address you bought it with; any clear statement that you want your money back is enough.
          The refund goes back to the payment method you used. Each Memoir can be refunded once: if
          you pay again for a Memoir you had refunded, that payment can be refunded only by writing
          to us. If you have handed the Memoir over (section 4), its purchase can no longer be
          refunded.
        </p>
        <p>
          A payment for a further year can also be refunded in full within 30 days of paying, for
          any reason, from the Memoir's page in the app or by writing to us. Refunding a year
          undoes it: the Memoir returns to the state it was in before that payment, with the same
          end date, the same rights and, if it had one, the same date it would have been deleted.
          Each paid year can be refunded once from the app; a later payment for the same year can
          be refunded only by writing to us. A year is also undone if its payment is reversed
          through your bank.
        </p>
        <p>
          This covers, and goes beyond, the 14-day right of withdrawal that consumers in the EU
          and the UK have by law, and it applies whether you buy as a consumer or as a business.
        </p>
        <p>
          If a purchase is refunded in full, or its payment is reversed through your bank, before
          the Memoir has been handed over, that Memoir becomes read-only, and an invitation to
          share it ends: you can no longer upload to it, but you can still view,
          share, download and delete everything already in it. A Memoir that is no longer paid
          for is not kept indefinitely: we email you, and if it has not been paid for again 30
          days later, we delete it and everything in it. While a payment dispute is open, nothing
          is deleted; the 30 days start only if the dispute ends with the payment reversed. Your
          other Memoirs are not affected. Once a Memoir has been handed over, a refund or reversal
          of a payment its previous owner made does not change it.
        </p>
      </section>

      <section>
        <h2>7. How long we keep your Memoir</h2>
        <p>
          A Memoir is kept for as long as each of its years is paid for (section 2). Nothing in it
          is deleted unless you delete it, a year is not paid for, its purchase is refunded or
          reversed (section 6), or you close your account.
        </p>
        <p>
          If a year is not paid for by the time the previous one ends, the Memoir becomes
          read-only for one more year: you can still view, share, download and delete everything
          in it, but not add to it. We email you once a month during that year, and a last time a
          few days before the end. If it is still not paid for at the end of that year, we delete
          it and everything in it. If we only notice late that a year was not paid, you still get
          at least 30 days' notice before anything is deleted. Paying during that year restores
          the Memoir; the payment covers the year you are in, so its anniversary does not change.
        </p>
        <p>
          If we ever decide to close Memoar, we will tell you by email at least 90 days before, so
          that you have time to download everything.
        </p>
        <p>
          To close your account, use Delete my account on the Account page. We delete your
          account, your Memoirs and everything in them within 30 days of your request, and tell
          you the exact date. Until then you can cancel, and your Memoirs are read-only: you can
          view and download everything, but not upload. On the last day you can only cancel. A
          payment that reaches us for an account being closed is refunded. A Memoir you have
          shared with a client who has accepted (section 4) is not deleted: it becomes theirs
          instead, unless its purchase has been refunded or disputed, or the client is closing
          their own account too, in which case it is deleted with yours.
        </p>
        <p>
          You can also write to <ContactEmail /> from the email address you sign in with. We then
          delete your account, your Memoirs and everything in them within 30 days of your request,
          and tell you the date.
        </p>
        <p>
          The deletion includes the albums you shared: from the last day they no longer open for
          the people you shared them with. Deleted photographs cannot be recovered, so download
          what you want to keep first. We keep only the purchase records that tax law requires, as
          described in the Privacy Policy.
        </p>
      </section>

      <section>
        <h2>8. Keep your originals</h2>
        <p>
          We store your files with care, but Memoar is a way to deliver and keep photographs, not
          a backup. Keep your own copies of anything you cannot afford to lose. You can download
          the contents of any album as a ZIP file at any time.
        </p>
      </section>

      <section>
        <h2>9. Availability and liability</h2>
        <p>
          We work to keep Memoar available but cannot promise it will always be uninterrupted or
          free of errors. To the extent the law allows, our total liability to you for any claim
          relating to Memoar is limited to the amount you paid us in the twelve months before the
          claim. Nothing in these terms limits a right that the law gives you as a consumer and
          does not allow a contract to remove.
        </p>
      </section>

      <section>
        <h2>10. Changes and governing law</h2>
        <p>
          If we change these terms we will update the date above, and tell you by email before a
          change that affects you materially takes effect. These terms are governed by{' '}
          <Fact value={LEGAL.governingLaw} label="governing law and courts" />. If you are a
          consumer, you keep the protection of the mandatory law of the country where you live.
        </p>
      </section>

      <section>
        <h2>11. Data processing terms for photographers</h2>
        <p>
          When you use Memoar in your business to store and deliver photographs of other people,
          such as your clients, you are the controller of the personal data in those photographs
          and we process it on your behalf. This section is the data processing agreement
          required by Article 28 of the GDPR (and its UK equivalent). It forms part of these
          terms, so there is nothing separate to sign. After you hand a Memoir over to your client
          (section 4), the client is its owner and the one who decides about the data in it.
        </p>
        <ul>
          <li>
            <strong>Scope.</strong> The data is whatever you upload: photographs and other files,
            their names and the album names you choose, which may show or describe your clients
            and anyone else in your pictures. We process it to store it, make display copies,
            and show and deliver it to you and to the people you share it with, for as long as it
            stays in your Memoir.
          </li>
          <li>
            <strong>Your instructions.</strong> We process it only to provide Memoar as you use
            it, which is your documented instruction, unless the law requires otherwise; in that
            case we tell you first, unless the law forbids it.
          </li>
          <li>
            <strong>Confidentiality and security.</strong> Only people bound by confidentiality
            have access to it. We protect it with the measures described in the Privacy Policy,
            and we tell you without undue delay if we become aware of a breach affecting it.
          </li>
          <li>
            <strong>Sub-processors.</strong> You authorise the providers listed in the Privacy
            Policy. We bind each of them to data protection obligations equivalent to these, and
            we remain responsible for them. We will announce any new provider by updating that
            list and emailing you at least 30 days before it starts processing your data; if you
            object and we cannot resolve it, you may close your account and receive a refund of
            any Memoir bought in the previous 12 months.
          </li>
          <li>
            <strong>Transfers.</strong> Where data leaves the European Economic Area, it is
            protected as described in the Privacy Policy: under the EU-US Data Privacy Framework
            or the European Commission's Standard Contractual Clauses.
          </li>
          <li>
            <strong>Helping you.</strong> We help you answer requests from the people in your
            photographs and meet your own security, breach-notification and impact-assessment
            obligations, as far as we reasonably can given what we process. Most requests you can
            fulfil yourself in the app, by downloading, renaming or deleting.
          </li>
          <li>
            <strong>At the end.</strong> When you delete content or close your account, we delete
            it as described in section 7. You can download everything beforehand.
          </li>
          <li>
            <strong>Audits.</strong> We give you the information you reasonably need to show that
            these obligations are met, and answer reasonable written questions about them.
          </li>
        </ul>
      </section>
    </LegalPage>
  )
}
